import zionDebug from '@fs/zion-debug'
import { useFeatureFlags } from '@fs/zion-flags'
import { i18n } from '@fs/zion-locale'
import { useQueryParams } from '@fs/zion-router'
import { getImage, getMostRecentFieldValue, useSlsStateContext } from '@fs/zion-sls'
import { Alert, Button, Divider, Paragraph, Separator, useOverlay } from '@fs/zion-ui'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import searchRecordImg from '../../images/search-record.png'
import { useFullTextStateContext, useFullTextDispatchContext } from './providers/FullTextContext'
import { useR2Highlights, useR2StateContext } from '../../providers/r2Provider/R2Context'
import { useOverlayContext } from '../../../shared/providers/overlayContextProvider/OverlayContext'
import { useSharedBrowserStateContext } from '../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import HighlightPanelOld from '../../components/generic/panels/highlightPanelOld/HighlightPanelOld'
import useStatusOverlayTrigger from '../../hooks/useStatusOverlayTrigger'
import useTreeAttachmentPrompts from '../../hooks/useTreeAttachmentPrompts'
import useOpenAttachToTree from '../../hooks/useOpenAttachToTree'
import DownloadOverlay from './components/downloadOverlay/DownloadOverlay'
import useDownloadOverlayData from './hooks/useDownloadOverlayData'
import Transcript from './components/transcript/Transcript'
import hasMeaningfulContent from './utils/hasMeaningfulContent'
import { parseAiResponse } from './utils/parseAiResponse'
import useTranslate from '../../hooks/useTranslate'
import { getSourceLanguage } from '../../hooks/useTranslate.utils'
import {
  buildKeywordFields,
  fetchSummarization as fetchSummarizationUtil,
  getImagePermissionsBoolean,
  getDownloadPermission,
  fetchArtifact,
} from './FullText.utils'
import NoTranscriptMessage from './components/noTranscriptMessage/NoTranscriptMessage'
import FullTextToolbar from './components/fullTextToolbar/FullTextToolbar'
import FullTextActions from './components/fullTextActions/FullTextActions'
import FullTextSummarization from './components/fullTextSummarization/FullTextSummarization'
import AIPersonsExpander from './components/AiIndexing/AIPersonsExpander'
import AddNamesPanel from './components/addNamesPanel/AddNamesPanel'
import Translation from './components/translation/Translation'
import { useNamesExperienceToggle } from '../../hooks/useNamesExperienceOptIn'

const debug = zionDebug('rip:FullText')

const FullText = () => {
  const [t] = useTranslation()
  const {
    rip_sharedImageViewer_attachTreePrompt: attachTreePromptFlag,
    rip_sharedImageViewer_azure41StreamingPrompt: azure41StreamingPromptFlag,
    rip_sharedImageViewer_openAiGPT5: openAiGPT5Flag,
    rip_sharedImageViewer_autoIndexing: autoIndexingFlag,
    rip_sharedImageViewer_showTranscriptEditButton: showTranscriptEditButton,
    rip_sharedImageViewer_transcriptFromStuffRecords: transcriptFromStuffRecordsFlag,
  } = useFeatureFlags([
    'rip_sharedImageViewer_attachTreePrompt',
    'rip_sharedImageViewer_azure41StreamingPrompt',
    'rip_sharedImageViewer_openAiGPT5',
    'rip_sharedImageViewer_autoIndexing',
    'rip_sharedImageViewer_showTranscriptEditButton',
    'rip_sharedImageViewer_transcriptFromStuffRecords',
  ])
  const { isOptedIn } = useNamesExperienceToggle()

  const downloadOverlay = useOverlay()
  const state = useSlsStateContext()
  const fullTextState = useFullTextStateContext()
  const r2State = useR2StateContext()
  const fullTextDispatch = useFullTextDispatchContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const { query } = useQueryParams()
  const { displayHighlights } = useR2Highlights()
  const { triggerStatusOverlay } = useStatusOverlayTrigger()

  const [showKeywords, setShowKeywords] = useState(true)
  const [allHighlights, setAllHighlights] = useState([])
  const [isExpanderOpen, setIsExpanderOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [summarizationData, setSummarizationData] = useState(null) // Holds the current image's transcript data
  const [cachedSummarizations, setCachedSummarizations] = useState({}) // Cache for all fetched transcripts
  const [summarization] = useState('Here is the response we get from OpenAI')
  const [artifact, setArtifact] = useState()
  const [artifactLoadErr, setArtifactLoadErr] = useState()
  const [showingTranslation, setShowingTranslation] = useState(false)
  const [summaryError, setSummaryError] = useState(false)
  const [isEditingUnavailableDismissed, setEditingUnavailableDismissed] = useState(
    localStorage.getItem('editUnavailableMessageDismissed', 'true')
  )

  const [isEditHelpDismissed, setIsEditHelpDismissed] = useState(localStorage.getItem('editHelpDismissed', 'true'))
  const [streamingContent, setStreamingContent] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [isRequestInProgress, setIsRequestInProgress] = useState(false) // State to prevent concurrent requests
  const overlay = useOverlay()
  const { inPanelStack } = useOverlayContext()
  const openAttachToTree = useOpenAttachToTree()

  // Allow for alternate message if transcript is not available.
  const noTranscriptMessage = !fullTextState.imagePermission
    ? t('full-text.transcription-image-permission-false', 'Transcript is not available, image restricted.')
    : t('full-text.transcription-not-available', 'Transcript is not available.')
  const shouldFetch = useRef(false) // Ref to control the fetch behavior

  const {
    citation,
    plainTextTranscript,
    fields,
    imageStuffError,
    isTranscriptReady,
    recordPlace,
    recordDate,
    imageClassification,
    recordLanguage,
  } = useDownloadOverlayData(setAllHighlights)

  // If a token has redacted:true AND text is present in the SLS response, the user has elevated permissions.
  // Without permissions, redacted tokens are returned without text.
  const canViewRedactedContent = fields.some((field) => field.redacted && getMostRecentFieldValue(field))
  const handleExpanderToggle = useCallback(() => {
    setIsExpanderOpen((prev) => !prev)
    shouldFetch.current = true // Allow fetch when user toggles expander
  }, [])

  const { hasTranslationError, translationCache, isTranslating } = useTranslate({
    isTranslationEnabled: showingTranslation,
    transcriptText: plainTextTranscript,
    artifact,
    group: r2State?.groupData,
    isTranscriptReady,
  })

  const handleEditModeToggle = useCallback(() => {
    fullTextDispatch({
      type: 'SET_TRANSCRIPT_EDIT_MODE',
      value: !fullTextState.editTranscriptMode,
    })
    if (!isEditHelpDismissed) {
      setIsEditHelpDismissed(true)
      localStorage.setItem('editHelpDismissed', 'true')
    }
  }, [fullTextState.editTranscriptMode, fullTextDispatch, isEditHelpDismissed])

  const currentImageArk = useMemo(() => state.currentImageArk, [state.currentImageArk])
  // Tracks the image the user is actually viewing so an in-flight summarization
  // request for a since-abandoned image can't overwrite the current one when it resolves.
  const currentImageArkRef = useRef(currentImageArk)
  currentImageArkRef.current = currentImageArk

  const keywords = useMemo(
    () => query.keywords?.split(',').map((keyword) => decodeURIComponent(keyword)?.toLowerCase()) || [],
    [query.keywords]
  )

  const currentImage = useMemo(() => getImage(state?.images, state?.currentImageArk) || {}, [state])

  const keywordFields = useMemo(() => {
    return buildKeywordFields(keywords, fields)
  }, [keywords, fields])

  useEffect(() => {
    setIsExpanderOpen(false)
    setSummarizationData(null) // Reset transcript data when navigating to a new image
    shouldFetch.current = false // Reset the fetch control
    setSummaryError(false) // Reset error
    setStreamingContent('') // Reset streaming content
    setIsStreaming(false) // Reset streaming state
    setIsRequestInProgress(false) // Reset request flag
  }, [state.currentImageArk])

  // Determine the language for the artifact with fallback to group language or 'en'
  const artifactLanguage = getSourceLanguage(artifact, r2State?.groupData)

  const fetchSummarization = useCallback(async () => {
    // Prevent multiple concurrent requests
    if (isRequestInProgress) {
      return
    }

    setIsRequestInProgress(true)

    try {
      await fetchSummarizationUtil({
        currentImageArk,
        currentImageArkRef,
        plainTextTranscript,
        cachedSummarizations,
        setSummarizationData,
        setCachedSummarizations,
        setLoading,
        setSummaryError,
        azure41StreamingPromptFlagIsOn: azure41StreamingPromptFlag.isOn,
        openAiGPT5FlagIsOn: openAiGPT5Flag.isOn,
        parseTranscript: parseAiResponse,
        hasMeaningfulContent,
        setStreamingContent,
        setIsStreaming,
        coverages: artifact?.coverages,
        artifactLanguage,
        recordPlace,
        recordDate,
        imageClassification,
        recordLanguage,
        outputLanguage: i18n.language,
      })
    } finally {
      setIsRequestInProgress(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentImageArk,
    plainTextTranscript,
    cachedSummarizations,
    azure41StreamingPromptFlag.isOn,
    openAiGPT5Flag.isOn,
    artifact?.coverages,
    artifactLanguage,
    recordPlace,
    recordDate,
    imageClassification,
    recordLanguage,
    i18n.language,
  ])

  const onEditHelpDismiss = () => {
    setIsEditHelpDismissed(true)
    localStorage.setItem('editHelpDismissed', 'true')
  }

  const onEditingUnavailableDismiss = () => {
    setEditingUnavailableDismissed(true)
    localStorage.setItem('editUnavailableMessageDismissed', 'true')
  }

  useEffect(() => {
    if (!isExpanderOpen) return
    fetchSummarization()
  }, [isExpanderOpen, fetchSummarization])

  useEffect(() => {
    getImagePermissionsBoolean(currentImageArk, fullTextDispatch)
    getDownloadPermission(currentImageArk, fullTextDispatch)
  }, [currentImageArk, fullTextDispatch])

  useEffect(() => {
    // When https://fhjira.churchofjesuschrist.org/browse/RS-1469 is done, we can remove this call
    fetchArtifact(thumbnails, state.currentImageArk, setArtifact, setArtifactLoadErr)
  }, [thumbnails, state.currentImageArk])

  useEffect(() => {
    fullTextDispatch({
      type: 'SET_TRANSCRIPT_EDIT_MODE',
      value: false,
    })
  }, [currentImageArk, fullTextDispatch])

  const { startViewTimer } = useTreeAttachmentPrompts({
    // FSS-14672: attach-to-tree always goes straight to the classic 4-step flow
    openAttachToTree,
  })

  useEffect(() => {
    // Only show timer prompt if opted into the updated Names experience
    if (currentImage && attachTreePromptFlag?.isOn && query.view === 'fullText' && isOptedIn) {
      startViewTimer()
    }
  }, [currentImage, startViewTimer, attachTreePromptFlag?.isOn, query.view, isOptedIn])

  const showEditHelp = !isEditHelpDismissed

  // Enable translation when the artifact language is different from the user's current language,
  // and there are no errors, and there is a transcript to translate
  const noTranscript = fields?.length === 0 || !fields || !artifact || artifactLoadErr || imageStuffError
  const enableTranslation = artifactLanguage !== i18n.language && !hasTranslationError && !noTranscript

  debug('Render: ', {
    keywords,
    keywordFields,
    allHighlights,
    fields,
    artifactLanguage,
    groupLanguage: r2State?.groupData?.languages?.[0],
    enableTranslation,
  })

  return (
    <>
      {/* If a transcript is not available, an additional condition can be added to render the message. */}
      {!fullTextState.imagePermission && (
        <NoTranscriptMessage message={noTranscriptMessage} searchRecordImg={searchRecordImg} />
      )}
      {fullTextState.imagePermission && (
        <>
          {!isOptedIn && !fullTextState.editTranscriptMode && (
            <>
              <Separator size="xs" />
              <FullTextActions showKeywords={showKeywords} setShowKeywords={setShowKeywords} />
              <Separator size="xs" />
            </>
          )}

          <Divider />
          {fullTextState.editTranscriptMode && (
            <Alert type="help" dismissable>
              <Paragraph size="sm">{t('full-text.click-to-edit', 'Click on a word to edit.')}</Paragraph>
            </Alert>
          )}
          {autoIndexingFlag.isOn && hasMeaningfulContent(plainTextTranscript) && !fullTextState.editTranscriptMode && (
            <>
              <AIPersonsExpander plainTextTranscript={plainTextTranscript} />
              <Divider />
            </>
          )}
          {hasMeaningfulContent(plainTextTranscript) && !fullTextState.editTranscriptMode && (
            <FullTextSummarization
              stateCurrentImageArk={state.currentImageArk}
              overlay={overlay}
              currentImageArk={currentImageArk}
              isExpanderOpen={isExpanderOpen}
              handleExpanderToggle={handleExpanderToggle}
              summarization={summarization}
              summarizationData={summarizationData}
              summaryError={summaryError}
              fetchSummarization={fetchSummarization}
              loading={loading}
              streamingContent={streamingContent}
              isStreaming={isStreaming}
            />
          )}
          <Separator size="sm" />
          <FullTextToolbar
            plainTextTranscript={plainTextTranscript}
            downloadOverlay={downloadOverlay}
            fullTextDispatch={fullTextDispatch}
            fullTextState={fullTextState}
            handleEditModeToggle={handleEditModeToggle}
            showKeywords={showKeywords}
            setShowKeywords={setShowKeywords}
          />
          <Separator size="xs" />
          {showEditHelp && (
            <>
              {transcriptFromStuffRecordsFlag.isOn && !isEditingUnavailableDismissed && (
                <Alert type="help" dismissable onDismiss={onEditingUnavailableDismiss}>
                  {t('full-text.edit-unavailable', 'Editing is unavailable in Full-Text Search.')}{' '}
                  <Button
                    to="https://www.familysearch.org/help/helpcenter/article/why-transcript-editing-is-unavailable-in-full-text-search"
                    linkName="transcript-edit-learn-more"
                    target="_blank"
                  >
                    {t('full-text.learn-more', 'Learn More')}
                  </Button>
                </Alert>
              )}
              {showTranscriptEditButton.isOn && !isEditHelpDismissed && (
                <Alert type="help" dismissable onDismiss={onEditHelpDismiss}>
                  {t('full-text.edit-help', 'Click the pencil above to make edits to the transcript.')}
                </Alert>
              )}
              <Separator size="xs" />
            </>
          )}
          {fields.some((field) => field.redacted) && (
            <>
              <Alert type="warning">
                {canViewRedactedContent
                  ? t(
                      'full-text.redaction-notice-privileged',
                      'This record contains redacted content shown in red. You can see it because you have elevated permissions.'
                    )
                  : t(
                      'full-text.redaction-notice',
                      'Some information in this record has been redacted in accordance with applicable legal and policy requirements indicated as "■■■■■".'
                    )}
              </Alert>
              <Separator size="xs" />
            </>
          )}
          <Translation
            translationCache={translationCache}
            currentImageArk={state.currentImageArk}
            isTranslationAvailable={enableTranslation}
            isTranslationEnabled={showingTranslation}
            onToggleTranslation={() => setShowingTranslation((prev) => !prev)}
            hasTranslationError={hasTranslationError}
            hasArtifactError={artifactLoadErr}
            isTranslating={isTranslating}
            hasTranscriptText={!!plainTextTranscript}
            isTranscriptReady={isTranscriptReady}
          />

          {!showingTranslation && imageStuffError && (
            <Alert type="error">
              {t('full-text.transcript-text-error', 'Error loading transcript text. Please try again later.')}
            </Alert>
          )}

          {!showingTranslation && !imageStuffError && (
            <Transcript
              fields={fields}
              canViewRedactedContent={canViewRedactedContent}
              allHighlights={allHighlights}
              showKeywords={showKeywords}
              keywordFields={keywordFields}
            />
          )}

          <DownloadOverlay
            downloadOverlay={downloadOverlay}
            displayHighlights={displayHighlights}
            triggerStatusOverlay={triggerStatusOverlay}
            citation={citation}
            plainTextTranscript={plainTextTranscript}
            query={query}
          />

          {inPanelStack('addNamesOverlay') && <AddNamesPanel />}

          {inPanelStack('highlightsPanelOld') && <HighlightPanelOld />}
        </>
      )}
    </>
  )
}

export default FullText
